/**
 * Trips List Page Component
 * /admin/trips
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../api/client';
import ExportMenu from '../components/ExportMenu';

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadTrips();
    loadVehicles();
    loadDrivers();
  }, []);

  useEffect(() => {
    filterTrips();
  }, [statusFilter, searchTerm, vehicleFilter, driverFilter, startDateFilter, endDateFilter, allTrips]);

  const loadVehicles = async () => {
    try {
      const response = await apiClient.getVehicles({ status: 'active' });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await apiClient.getDrivers({ status: 'active' });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setDrivers(data);
    } catch (err) {
      console.error('Failed to load drivers:', err);
    }
  };

  const loadTrips = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (vehicleFilter) params.vehicle = vehicleFilter;
      if (driverFilter) params.driver = driverFilter;
      if (startDateFilter) params.start_date = startDateFilter;
      if (endDateFilter) params.end_date = endDateFilter;
      
      const response = await apiClient.getTrips(params);
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setAllTrips(data);
    } catch (err) {
      setError(err.message || 'Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const filterTrips = () => {
    let filtered = [...allTrips];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Apply vehicle filter
    if (vehicleFilter) {
      filtered = filtered.filter(t => 
        t.vehicle === vehicleFilter || 
        t.vehicle_detail?.id === vehicleFilter ||
        t.vehicle_detail?.plate_number === vehicleFilter
      );
    }

    // Apply driver filter
    if (driverFilter) {
      filtered = filtered.filter(t => 
        t.driver === driverFilter || 
        t.driver_detail?.id === driverFilter
      );
    }

    // Apply date range filter
    if (startDateFilter) {
      const startDate = new Date(startDateFilter);
      filtered = filtered.filter(t => {
        if (!t.started_at) return false;
        const tripDate = new Date(t.started_at);
        return tripDate >= startDate;
      });
    }

    if (endDateFilter) {
      const endDate = new Date(endDateFilter);
      endDate.setHours(23, 59, 59, 999); // Include entire end date
      filtered = filtered.filter(t => {
        if (!t.started_at) return false;
        const tripDate = new Date(t.started_at);
        return tripDate <= endDate;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(t => {
        const bookingRef = (t.booking_detail?.booking_reference || t.booking || '').toLowerCase();
        const vehiclePlate = (t.vehicle_detail?.plate_number || t.vehicle || '').toLowerCase();
        const driverName = (t.driver_detail?.full_name || t.driver || '').toLowerCase();
        const tripId = (t.id || '').toLowerCase();
        return (
          bookingRef.includes(searchLower) ||
          vehiclePlate.includes(searchLower) ||
          driverName.includes(searchLower) ||
          tripId.includes(searchLower)
        );
      });
    }

    setTrips(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadTrips();
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setVehicleFilter('');
    setDriverFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSearchTerm('');
    loadTrips();
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'info',
      in_progress: 'primary',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading && trips.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Trip Logs
        </Typography>
        <ExportMenu
          resource="bookings/trips"
          params={{
            search: searchTerm,
            status: statusFilter !== 'all' ? statusFilter : '',
            vehicle: vehicleFilter,
            driver: driverFilter,
            start_date: startDateFilter,
            end_date: endDateFilter,
          }}
          onError={setError}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                placeholder="Search trips by booking, vehicle, driver..."
                value={searchTerm}
                onChange={handleSearch}
                slotProps={{ input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                } }}
              />
            </form>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            {(statusFilter !== 'all' || vehicleFilter || driverFilter || startDateFilter || endDateFilter || searchTerm) && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Vehicle</InputLabel>
              <Select
                value={vehicleFilter}
                label="Vehicle"
                onChange={(e) => setVehicleFilter(e.target.value)}
              >
                <MenuItem value="">All Vehicles</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.plate_number} - {vehicle.make} {vehicle.model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Driver</InputLabel>
              <Select
                value={driverFilter}
                label="Driver"
                onChange={(e) => setDriverFilter(e.target.value)}
              >
                <MenuItem value="">All Drivers</MenuItem>
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.full_name || `${driver.first_name} ${driver.last_name}` || driver.id.substring(0, 8)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Trip ID</TableCell>
              <TableCell>Booking</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Distance (km)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No trips found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              trips.map((trip) => (
                <TableRow key={trip.id} hover>
                  <TableCell>{trip.id.substring(0, 8)}</TableCell>
                  <TableCell>
                    {trip.booking_detail?.booking_reference || trip.booking || '-'}
                  </TableCell>
                  <TableCell>
                    {trip.vehicle_detail?.plate_number || trip.vehicle || '-'}
                  </TableCell>
                  <TableCell>
                    {trip.driver_detail?.full_name || trip.driver || '-'}
                  </TableCell>
                  <TableCell>{formatDate(trip.started_at)}</TableCell>
                  <TableCell>{formatDate(trip.completed_at)}</TableCell>
                  <TableCell>
                    {trip.actual_distance != null && typeof trip.actual_distance === 'number' 
                      ? `${trip.actual_distance.toFixed(2)} km` 
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={trip.status || 'scheduled'}
                      color={getStatusColor(trip.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/trips/${trip.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Trips;

